import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, User, Mail, Phone, FileText } from 'lucide-react';
import { submitLeadToFollowUpBoss, type LeadData } from '../utils/crmIntegration';
import { trackModalOpen, trackModalClose, trackButtonClick } from '../utils/gtmTracking';

interface InfoRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const InfoRequestModal: React.FC<InfoRequestModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Track modal open
  React.useEffect(() => {
    if (isOpen) {
      trackModalOpen('info_request_modal', 'learn_more_button');
    }
  }, [isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    trackButtonClick('Send Me the Information', 'info_request_modal', 'form_submission');
    
    try {
      // Prepare lead data for Follow Up Boss
      const leadData: LeadData = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        message: `Information request from ${formData.firstName} ${formData.lastName}. Requested career information packet about KILOTERRA land sales opportunities.`,
        source: 'Careers - Learn More', // Added "Careers" prefix
        leadType: 'info_request',
        timestamp: new Date().toISOString()
      };

      // Submit to Follow Up Boss
      console.log('🚀 Submitting info request to Follow Up Boss...');
      const success = await submitLeadToFollowUpBoss(leadData);
      
      console.log('✅ Info request submitted successfully:', { 
        contact: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        fubSuccess: success,
        redirecting: 'to thank-you page'
      });
      
      // Close modal and redirect to thank you page
      onClose();
      navigate('/thank-you?type=info_request&source=learn_more');

    } catch (error) {
      console.error('❌ Info request submission error:', error);
      // Still redirect to thank you page even if there's an error
      onClose();
      navigate('/thank-you?type=info_request&source=learn_more');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      trackModalClose('info_request_modal', isSubmitted);
      setFormData({ firstName: '', lastName: '', email: '', phone: '' });
      setIsSubmitted(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity duration-300"
        onClick={handleClose}
      ></div>
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white w-full max-w-md transform transition-all duration-300 scale-100 opacity-100 shadow-2xl">
          {/* Close Button */}
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
          >
            <X className="h-6 w-6" />
          </button>

          {/* Header */}
          <div className="bg-kiloterra-dark px-6 py-8 text-center">
            <div className="flex items-center justify-center mb-4">
              <img 
                src="/kiloterra_logo_stacked_simple_grey.png" 
                alt="KILOTERRA Logo" 
                className="h-8 w-auto brightness-0 invert"
              />
            </div>
            <h2 className="text-2xl font-bold text-white uppercase">
              Get More Information
            </h2>
            <p className="text-gray-200 mt-2">
              Provide your email address below, and we'll send you additional information about our Land Specialist opportunities.
            </p>
          </div>

          {/* Form Content */}
          <div className="px-6 py-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 uppercase">
                    First Name *
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                      disabled={isSubmitting}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 focus:ring-2 focus:ring-kiloterra-dark focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="First name"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 uppercase">
                    Last Name *
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                      disabled={isSubmitting}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 focus:ring-2 focus:ring-kiloterra-dark focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="Last name"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 uppercase">
                  Email Address *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 focus:ring-2 focus:ring-kiloterra-dark focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="your.email@example.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 uppercase">
                  Phone Number *
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 focus:ring-2 focus:ring-kiloterra-dark focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>

              {/* What they'll receive - UPDATED CHECKLIST */}
              <div className="bg-gray-50 p-4 border-l-4 border-kiloterra-yellow">
                <div className="flex items-start">
                  <FileText className="h-5 w-5 text-kiloterra-dark mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-kiloterra-dark text-sm uppercase mb-2">
                      You'll Receive:
                    </h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Comprehensive FAQ document</li>
                      <li>• Startup Process Outline</li>
                      <li>• Equipment Checklist</li>
                      <li>• Career Benefits Overview</li>
                      <li>• Training program overview</li>
                    </ul>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-kiloterra-yellow text-kiloterra-dark py-4 font-bold text-lg hover:bg-opacity-90 transition-all duration-300 transform hover:scale-105 disabled:transform-none disabled:hover:bg-kiloterra-yellow disabled:opacity-70 disabled:cursor-not-allowed uppercase tracking-wide"
              >
                {isSubmitting ? 'Sending...' : 'Send Me the Information'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InfoRequestModal;